import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Spinner, Surface, PageHeader } from "@/components/common";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CheckCircle2, AlertTriangle, BookOpen, RotateCcw, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { FilterBar, FunnelView, Kpi, fmtDKK, fmtDate, fmtNum, PLAN_LABELS } from "./parts";

const SRC_LABELS = { facebook: "Facebook", linkedin: "LinkedIn", google: "Google", "direkte / ukendt": "Direkte / ukendt" };
const fmtSeconds = (s) => {
  if (s == null) return "—";
  if (s < 60) return `${Math.round(s)} sek.`;
  const m = Math.floor(s / 60), r = Math.round(s % 60);
  return `${m}m ${r}s`;
};

const DEFS = [
  ["Besøgende", "Unikke anonymous_id. Kun i GA4 — ikke gemt serverside (vises som —)."],
  ["Nye konti", "Unikke brugere med sign_up. Admin/test ekskluderet som standard."],
  ["Trial startet", "Unikke Stripe-abonnementer med trial_started."],
  ["Ny betalende kunde", "Første positive betaling pr. virksomhed."],
  ["MRR", "Månedlig abonnementsomsætning ekskl. moms fra aktive betalende abonnementer. Free indgår ikke."],
  ["Trial-to-paid", "Prøveperioder med første positive betaling divideret med modne/afsluttede prøveperioder. Aktive prøveperioder tælles ikke som tabte."],
  ["Aktiveret virksomhed", "Virksomhed med mindst én gennemført generering efter oprettelse."],
  ["Aktiv virksomhed", "Virksomhed med mindst én tilknyttet eksisterende bruger."],
];

export default function AdminTracking() {
  const [filters, setFilters] = useState({ days: 30, plan: "", show_test: false });
  const [tab, setTab] = useState("funnel");
  useEffect(() => {
    if (tab === "subs" && filters.plan === "gratis") setFilters((f) => ({ ...f, plan: "" }));
  }, [tab, filters.plan]);
  const [ov, setOv] = useState(null);
  const [subs, setSubs] = useState(null);
  const [analyses, setAnalyses] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [defs, setDefs] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [syncing, setSyncing] = useState(false);

  const [speed, setSpeed] = useState(null);
  const [speedRunning, setSpeedRunning] = useState(false);
  const runSpeedTest = async () => {
    setSpeedRunning(true);
    try {
      const { data } = await api.post("/admin/ai-speed-test");
      setSpeed(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSpeedRunning(false);
    }
  };

  const deleteAnalysis = async (r) => {
    if (!window.confirm(`Slet "${r.website || r.name}" fra listen?${r.kind === "company" ? " Kontoen og virksomheden bliver liggende." : ""}`)) return;
    try {
      await api.delete(`/admin/started-analyses/${r.kind}/${r.id}`);
      setAnalyses((list) => list.filter((x) => !(x.kind === r.kind && x.id === r.id)));
      toast.success("Analysen er slettet fra listen");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const doResync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/stripe-resync");
      toast.success(`${data.synced} abonnementer synkroniseret · ${data.trials_restored ?? 0} prøveperioder givet tilbage (af ${data.checked ?? 0} opsagte uden betaling)${data.failed ? ` · ${data.failed} fejlede` : ""}`, {
        description: (data.skipped || []).length ? `Ikke ændret: ${data.skipped.map((s) => `${s.email} (${s.reason})`).join(", ")}` : undefined,
        duration: (data.skipped || []).length ? 20000 : 4000,
      });
      setReloadKey((k) => k + 1);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    const p = new URLSearchParams({ days: filters.days, plan: filters.plan, show_test: filters.show_test });
    Promise.all([api.get(`/admin/overview?${p}`), api.get(`/admin/subscriptions?show_test=${filters.show_test}&plan=${filters.plan}`), api.get(`/admin/started-analyses`)])
      .then(([a, b, c]) => { setOv(a.data); setSubs(b.data); setAnalyses(c.data.analyses); setErr(null); })
      .catch((e) => setErr(apiError(e))).finally(() => setLoading(false));
  }, [filters, reloadKey]);

  const doReset = async () => {
    setResetting(true);
    try {
      const { data } = await api.post("/admin/reset-analytics");
      toast.success(`Analysedata nulstillet (${data.deleted_events + data.deleted_subscription_events} poster slettet)`);
      setResetOpen(false);
      setReloadKey((k) => k + 1);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setResetting(false);
    }
  };

  return (
    <div>
      <PageHeader overline="Administrator" title="Tracking" subtitle="Funnel, trafikkilder, abonnementer, produktbrug og datakvalitet."
        action={<div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setDefs(true)} data-testid="open-definitions" className="gap-1.5"><BookOpen className="h-3.5 w-3.5" /> Definitioner</Button>
          <Button variant="outline" size="sm" onClick={() => setResetOpen(true)} data-testid="reset-analytics-btn" className="gap-1.5 text-destructive hover:text-destructive"><RotateCcw className="h-3.5 w-3.5" /> Nulstil analysedata</Button>
        </div>} />
      <FilterBar filters={filters} setFilters={setFilters} hideFree={tab === "subs"} />

      {err ? <div className="py-16 text-center text-muted-foreground">{err}</div>
      : loading || !ov || !subs ? <Spinner label="Henter tracking..." /> : (
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-5 flex-wrap h-auto">
          <TabsTrigger value="funnel" data-testid="tab-funnel">Funnel</TabsTrigger>
          <TabsTrigger value="traffic" data-testid="tab-traffic">Trafikkilder</TabsTrigger>
          <TabsTrigger value="analyses" data-testid="tab-analyses">Påbegyndte analyser</TabsTrigger>
          <TabsTrigger value="subs" data-testid="tab-subs">Abonnementer</TabsTrigger>
          <TabsTrigger value="product" data-testid="tab-product">Produktbrug</TabsTrigger>
          <TabsTrigger value="dq" data-testid="tab-dq">Datakvalitet</TabsTrigger>
        </TabsList>

        <TabsContent value="funnel">
          <Surface><FunnelView steps={ov.funnel} />
            <p className="text-xs text-muted-foreground mt-4">Dedup: unikke enheder pr. trin (anonymous_id før konto, derefter user_id/company_id). GA4-kun-trin vises som — for ikke at estimere.</p></Surface>
        </TabsContent>

        <TabsContent value="traffic">
          <Surface className="mb-4">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div>
                <h3 className="font-display font-medium">Besøg pr. kilde</h3>
                <p className="text-xs text-muted-foreground mt-0.5">Andel af trafikken fra Facebook- vs LinkedIn-annoncering (kræver UTM-mærkede annoncelinks) samt gns. tid på kom-i-gang-siden.</p>
              </div>
              <div className="text-right flex gap-6">
                <div>
                  <p className="text-xs text-muted-foreground">Gns. tid på kom-i-gang</p>
                  <p className="font-display text-2xl font-medium" data-testid="komigang-avg-time">{fmtSeconds(ov.komigang_avg_seconds)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Hurtigt frafald (&lt;5s)</p>
                  <p className="font-display text-2xl font-medium" data-testid="komigang-bounce">{ov.komigang_bounce_pct != null ? `${ov.komigang_bounce_pct}%` : "—"}</p>
                </div>
              </div>
            </div>
            <div className="overflow-x-auto"><Table>
              <TableHeader><TableRow>
                <TableHead>Kilde</TableHead>
                <TableHead className="text-right">Besøgende</TableHead>
                <TableHead className="text-right">Andel</TableHead>
                <TableHead className="text-right">Analyse startet</TableHead>
                <TableHead className="text-right">Tilmeldinger</TableHead>
                <TableHead className="text-right">Gns. tid</TableHead>
                <TableHead className="text-right">Hurtigt frafald (&lt;5s)</TableHead>
              </TableRow></TableHeader>
              <TableBody>{(!ov.traffic_sources || ov.traffic_sources.length === 0) ? <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-6">Ingen besøgsdata endnu</TableCell></TableRow> :
                ov.traffic_sources.map((r, i) => <TableRow key={i} data-testid={`traffic-source-${r.source}`}>
                  <TableCell className="capitalize font-medium">{SRC_LABELS[r.source] || r.source}</TableCell>
                  <TableCell className="text-right font-mono">{fmtNum(r.visitors)}</TableCell>
                  <TableCell className="text-right font-mono">{r.share_pct != null ? `${r.share_pct}%` : "—"}</TableCell>
                  <TableCell className="text-right font-mono">{fmtNum(r.analyses_started)}</TableCell>
                  <TableCell className="text-right font-mono">{fmtNum(r.signups)}</TableCell>
                  <TableCell className="text-right font-mono">{fmtSeconds(r.avg_seconds)}</TableCell>
                  <TableCell className="text-right font-mono">{r.bounce_pct != null ? `${r.bounce_pct}%` : "—"}</TableCell>
                </TableRow>)}
              </TableBody></Table></div>
            <p className="text-xs text-muted-foreground mt-3">Bemærk: kilde kan kun bestemmes for besøg med UTM-tags på linket. Eksempel: <code className="text-foreground">?utm_source=facebook&utm_medium=paid&utm_campaign=NAVN</code>. Uden tags samles trafikken under "Direkte / ukendt".</p>
          </Surface>
          <Surface>
            <p className="text-xs text-muted-foreground mb-3">Konverteringer pr. kilde (first-touch attribution på tilmeldte brugere). Manglende attribution samles under "direkte / ukendt".</p>
            <div className="overflow-x-auto"><Table>
              <TableHeader><TableRow><TableHead>Source / medium</TableHead><TableHead className="text-right">Nye konti</TableHead><TableHead className="text-right">Prøveperioder</TableHead><TableHead className="text-right">Betalinger</TableHead><TableHead className="text-right">Omsætning ekskl. moms</TableHead></TableRow></TableHeader>
              <TableBody>{ov.traffic.length === 0 ? <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6">Ingen data</TableCell></TableRow> :
                ov.traffic.map((r, i) => <TableRow key={i}><TableCell>{r.source} / {r.medium}</TableCell><TableCell className="text-right font-mono">{fmtNum(r.accounts)}</TableCell><TableCell className="text-right font-mono">{fmtNum(r.trials)}</TableCell><TableCell className="text-right font-mono">{fmtNum(r.payments)}</TableCell><TableCell className="text-right font-mono">{fmtDKK(r.revenue_excl_vat)}</TableCell></TableRow>)}
              </TableBody></Table></div>
          </Surface>
        </TabsContent>

        <TabsContent value="analyses">
          <Surface>
            <div className="mb-3">
              <div className="flex items-center justify-between gap-3">
                <h3 className="font-display font-medium">Påbegyndte analyser</h3>
                <Button variant="outline" size="sm" onClick={runSpeedTest} disabled={speedRunning} data-testid="ai-speed-test-btn">
                  {speedRunning ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Tester AI (op til 40 sek.)…</> : "Test AI-hastighed"}
                </Button>
              </div>
              {speed && (
                <div className="mt-3 rounded-md border border-border p-3 text-xs space-y-1" data-testid="ai-speed-test-result">
                  <div className="text-muted-foreground">Hjemmeside hentet på {speed.scrape_seconds}s · Server-CPU: {speed.cpu_limit ?? "ubegrænset"}</div>
                  {speed.results.map((r) => (
                    <div key={r.model} className="flex justify-between gap-4 font-mono">
                      <span>{r.model}</span>
                      <span className={r.error ? "text-red-600" : r.seconds > 10 ? "text-red-600 font-semibold" : "text-emerald-600 font-semibold"}>{r.error ? `Fejl efter ${r.seconds}s` : `${r.seconds}s`}</span>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-xs text-muted-foreground mt-0.5">Hver hjemmeside der er tastet ind i onboarding-guiden — både dem der <b>gik i stå</b> (aldrig fuldførte) og dem der oprettede en konto. Uafhængig af samtykke og team-IP, så jeres egne tests vises også.</p>
            </div>
            <div className="overflow-x-auto"><Table>
              <TableHeader><TableRow>
                <TableHead>Hjemmeside</TableHead>
                <TableHead>Virksomhed</TableHead>
                <TableHead>Kilde</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>E-mail</TableHead>
                <TableHead className="text-right">Tidspunkt</TableHead>
                <TableHead className="text-right whitespace-nowrap">Kortlægning</TableHead>
                <TableHead className="w-10" />
              </TableRow></TableHeader>
              <TableBody>{(!analyses || analyses.length === 0) ? <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-6">Ingen påbegyndte analyser endnu</TableCell></TableRow> :
                analyses.map((r, i) => <TableRow key={`${r.kind}-${r.id || i}`} data-testid={`started-analysis-${i}`}>
                  <TableCell className="font-medium break-all">{r.website || "—"}</TableCell>
                  <TableCell>{r.name || "—"}</TableCell>
                  <TableCell><Badge variant="outline" className="text-[10px] whitespace-nowrap" data-testid={`started-analysis-source-${i}`}>{r.source || "Ukendt"}</Badge></TableCell>
                  <TableCell>
                    {r.status === "gik_i_staa" ? <Badge variant="outline" className="text-amber-500 border-amber-500/40">Gik i stå</Badge>
                    : r.status === "afventer_bekraeftelse" ? <Badge variant="outline" className="text-muted-foreground">Afventer bekræftelse</Badge>
                    : <Badge variant="outline" className="text-emerald-500 border-emerald-500/40">Konto oprettet</Badge>}
                  </TableCell>
                  <TableCell className="text-muted-foreground">{r.email || "—"}</TableCell>
                  <TableCell className="text-right text-muted-foreground whitespace-nowrap">{fmtDate(r.created_at)}</TableCell>
                  <TableCell className="text-right font-mono text-xs whitespace-nowrap" data-testid={`started-analysis-timing-${i}`}>
                    {r.timing ? <span title={`Hjemmeside ${r.timing.scrape}s · AI ${r.timing.ai}s${r.timing.client != null ? ` · I browseren ${r.timing.client}s` : ""}`}>
                      <b className={(r.timing.client ?? r.timing.scrape + r.timing.ai) > 10 ? "text-red-600" : ""}>{r.timing.client ?? (r.timing.scrape + r.timing.ai).toFixed(1)}s</b>
                      <span className="text-muted-foreground"> ({r.timing.scrape}+{r.timing.ai})</span>
                    </span> : "—"}
                  </TableCell>
                  <TableCell className="text-right">
                    {r.id && <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-red-600" onClick={() => deleteAnalysis(r)} data-testid={`started-analysis-delete-${i}`} aria-label="Slet analyse"><Trash2 className="h-4 w-4" /></Button>}
                  </TableCell>
                </TableRow>)}
              </TableBody></Table></div>
            <p className="text-xs text-muted-foreground mt-3">"Gik i stå" = person indtastede en hjemmeside og fik en profil, men fuldførte aldrig tilmeldingen. Perfekt til at følge op på potentielle leads.</p>
          </Surface>
        </TabsContent>

        <TabsContent value="subs">
          <div className="flex justify-end mb-3">
            <Button variant="outline" size="sm" data-testid="admin-stripe-resync-btn" disabled={syncing} onClick={doResync}>
              {syncing ? "Synkroniserer…" : "Synk med Stripe"}
            </Button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3 mb-5">
            <Kpi label="Aktive prøver" value={subs.summary.active_trials} />
            <Kpi label="Udløber < 7 dage" value={subs.summary.expiring_7d} />
            <Kpi label="Trial-to-paid" value={subs.summary.trial_to_paid} tip="Kun på modne/afsluttede prøveperioder." />
            <Kpi label="Aktive betalende" value={subs.summary.active_paying} />
            <Kpi label="Opsigelser" value={subs.summary.cancellations} />
            <Kpi label="Mislykkede betalinger" value={subs.summary.failed_payments} />
            <Kpi label="MRR ekskl. moms" value={subs.summary.mrr_excl_vat} isMoney />
          </div>
          <Surface>
            <div className="overflow-x-auto"><Table>
              <TableHeader><TableRow><TableHead>Virksomhed</TableHead><TableHead>Plan</TableHead><TableHead>Status</TableHead><TableHead>Trial startet</TableHead><TableHead>Trial udløber</TableHead><TableHead>Første betaling</TableHead><TableHead>Opsigelse</TableHead><TableHead className="text-right">MRR ekskl. moms</TableHead></TableRow></TableHeader>
              <TableBody>{subs.rows.length === 0 ? <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-6">Ingen abonnementer</TableCell></TableRow> :
                subs.rows.map((r, i) => <TableRow key={i}><TableCell className="font-medium">{r.company}</TableCell><TableCell><Badge variant="secondary">{PLAN_LABELS[r.plan] || r.plan}</Badge></TableCell><TableCell className="text-xs">{r.status || "—"}</TableCell><TableCell className="text-xs text-muted-foreground">{fmtDate(r.trial_started_at)}</TableCell><TableCell className="text-xs text-muted-foreground">{fmtDate(r.trial_ends_at)}</TableCell><TableCell className="text-xs text-muted-foreground">{fmtDate(r.first_paid_at)}</TableCell><TableCell className="text-xs">{r.cancel_at_period_end ? "Ja" : "—"}</TableCell><TableCell className="text-right font-mono">{fmtDKK(r.mrr_excl_vat)}</TableCell></TableRow>)}
              </TableBody></Table></div>
          </Surface>
        </TabsContent>

        <TabsContent value="product">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            <Kpi label="Aktive virksomheder" value={ov.product.active_companies} tip="Virksomheder med mindst én tilknyttet eksisterende bruger." />
            <Kpi label="Aktiverede" value={ov.product.activated_companies} tip="Virksomheder med mindst én gennemført generering." />
            <Kpi label="Uden aktivitet" value={ov.product.no_activity_companies} />
            <Kpi label="Genereringer i alt" value={ov.product.total_generations} />
          </div>
          <Surface>
            <h3 className="font-display font-medium mb-3">Mest anvendte moduler</h3>
            {ov.product.modules.length === 0 ? <p className="text-sm text-muted-foreground">Ingen genereringer endnu.</p> : (
              <div className="space-y-2">{ov.product.modules.map((m) => {
                const max = Math.max(...ov.product.modules.map((x) => x.count), 1);
                return <div key={m.module} className="flex items-center gap-3"><div className="w-40 text-sm text-muted-foreground truncate">{m.module}</div>
                  <div className="flex-1 h-6 rounded bg-secondary/60"><div className="h-full rounded" style={{ width: `${(m.count / max) * 100}%`, background: "#1dddbf44", borderRight: "2px solid #1dddbf" }} /></div>
                  <div className="w-12 text-right text-sm font-mono">{fmtNum(m.count)}</div></div>;
              })}</div>
            )}
          </Surface>
        </TabsContent>

        <TabsContent value="dq">
          <div className="grid md:grid-cols-2 gap-4">
            <Surface>
              <h3 className="font-display font-medium mb-3">Stripe-webhooks</h3>
              <DqRow ok={!!ov.data_quality.last_webhook_at} label="Seneste webhook" val={ov.data_quality.last_webhook_at ? `${fmtDate(ov.data_quality.last_webhook_at, true)} (${ov.data_quality.last_webhook_type || "?"})` : "Ingen modtaget"} />
              <DqRow ok={ov.data_quality.failed_webhooks === 0} label="Fejlede/ubehandlede webhooks" val={fmtNum(ov.data_quality.failed_webhooks)} />
            </Surface>
            <Surface>
              <h3 className="font-display font-medium mb-3">Datakonsistens</h3>
              <DqRow ok={ov.data_quality.events_without_user === 0} label="Events uden bruger-ID" val={fmtNum(ov.data_quality.events_without_user)} />
              <DqRow ok label="Betalinger uden attribution" val={fmtNum(ov.data_quality.payments_without_attribution)} warnOnly />
              <DqRow ok={ov.data_quality.orphan_companies === 0} label="Forældreløse virksomheder (uden bruger)" val={fmtNum(ov.data_quality.orphan_companies)} warnOnly />
              <DqRow ok label="Testevents i databasen" val={fmtNum(ov.data_quality.test_events)} warnOnly />
            </Surface>
            <Surface className="md:col-span-2">
              <h3 className="font-display font-medium mb-3">Events fordelt på miljø (APP_ENV)</h3>
              <div className="flex gap-2 flex-wrap">{ov.data_quality.events_by_env.length === 0 ? <span className="text-sm text-muted-foreground">Ingen events.</span> :
                ov.data_quality.events_by_env.map((e) => <Badge key={e.env} variant="secondary">{e.env || "ukendt"}: {fmtNum(e.count)}</Badge>)}</div>
            </Surface>
          </div>
        </TabsContent>
      </Tabs>)}

      <Dialog open={defs} onOpenChange={setDefs}>
        <DialogContent className="max-w-lg" data-testid="definitions-dialog">
          <DialogHeader><DialogTitle>KPI-definitioner</DialogTitle></DialogHeader>
          <div className="space-y-3 py-1 max-h-[60vh] overflow-y-auto">
            {DEFS.map(([t, d]) => <div key={t}><p className="text-sm font-medium">{t}</p><p className="text-xs text-muted-foreground">{d}</p></div>)}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent className="max-w-md" data-testid="reset-analytics-dialog">
          <DialogHeader><DialogTitle>Nulstil analysedata?</DialogTitle></DialogHeader>
          <div className="space-y-3 py-1">
            <div className="flex items-start gap-2 text-sm text-muted-foreground">
              <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
              <p>Dette sletter <span className="text-foreground font-medium">alle</span> besøgs-/funneldata og abonnements-/omsætningsdata i dashboardet. Handlingen kan ikke fortrydes.</p>
            </div>
            <p className="text-xs text-muted-foreground">Brugere og virksomheder bevares. Data i Google Analytics (GA4) påvirkes ikke.</p>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" size="sm" onClick={() => setResetOpen(false)} disabled={resetting} data-testid="reset-cancel-btn">Annullér</Button>
            <Button size="sm" onClick={doReset} disabled={resetting} data-testid="reset-confirm-btn" className="gap-1.5 bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {resetting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />} Nulstil alt
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DqRow({ ok, label, val, warnOnly }) {
  const good = warnOnly ? true : ok;
  return (
    <div className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
      <span className="text-sm text-muted-foreground flex items-center gap-2">
        {good ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <AlertTriangle className="h-4 w-4 text-amber-500" />} {label}
      </span>
      <span className="text-sm font-mono">{val}</span>
    </div>
  );
}
